/**
 * TOSS Web Application - useClickOutside Hook
 *
 * Hook for detecting clicks outside of specified elements.
 * Useful for closing dropdowns, modals, and other overlay components.
 */

import { useEffect, useCallback, type RefObject } from 'react';

/**
 * Detects clicks outside of the referenced elements and calls the callback.
 *
 * @param refs - Array of refs to elements that should NOT trigger the callback
 * @param callback - Function to call when click is detected outside all refs
 * @param enabled - Whether the listener is active (default: true)
 *
 * @example
 * ```tsx
 * const dropdownRef = useRef<HTMLDivElement>(null);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 *
 * useClickOutside([dropdownRef, triggerRef], () => {
 *   setIsOpen(false);
 * }, isOpen);
 * ```
 */
export function useClickOutside(
  refs: RefObject<HTMLElement | null>[],
  callback: () => void,
  enabled: boolean = true
): void {
  const stableCallback = useCallback(callback, [callback]);

  useEffect(() => {
    if (!enabled) return;

    const handleClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      // Check if click is outside ALL referenced elements
      const isOutside = refs.every((ref) => {
        if (!ref.current) return true;
        return !ref.current.contains(target);
      });

      if (isOutside) {
        stableCallback();
      }
    };

    // Use mousedown and touchstart for immediate response
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [refs, stableCallback, enabled]);
}

export default useClickOutside;
